import { useLang } from '@/i18n';
import gtm from '@/utils/gtm';
import EcoIconSwiperArrow from 'eco-design-micro/icons/eco-icon-swiper-arrow';
import EditorLink from 'lowcode/components/EditorLink';
import { useRouter } from 'next/router';
import { FC, useEffect, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useProductPageStatus } from '../../ProductProvider';
import { IPageStatus } from '../../config/IPageStatus';
import Step1 from './Step1';
import Step2 from './Step2';
import Step3 from './Step3';
import Step4 from './Step4';
import Step5 from './Step5';
import Step6 from './Step6';
import Step7 from './Step7';
import Step8 from './Step8';
import css from './index.module.scss';
import { signupCookie } from '@/utils/cookies/signupCookie';

const MoreIcon = () => {
    return (
        <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6.33398 4.7002L10.334 8.7002L6.33398 12.7002" stroke="#3F68E0" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
};

const TheComp: FC = () => {
    const { locale } = useRouter();
    const { t } = useLang('rapidseries.service');
    const [gap, setGap] = useState(24);
    const pageStatus = useProductPageStatus();
    const [list, setList] = useState([
        {
            title: t('item1_title'),
            content: [t('item1_content1'), t('item1_content2')],
            icon: locale === 'jp' ? <Step5 /> : <Step1 />,
        },
        {
            title: t('item2_title'),
            content: [t('item2_content1')],
            icon: locale === 'jp' ? <Step6 /> : <Step2 />,
            learnMoreLink: t.rich('item2_learn_more', {
                link: (text: any) => (
                    <EditorLink
                        href={`https://${locale}.ecoflow.com/pages/warranty-policy`}
                        className="card-link GtmModuleData-btn-href link2 learn-more"
                        style={{ color: '#3F68E0' }}
                        target="_blank"
                        rel="noopener noreferrer"
                        dataLayer={{
                            __btn_id1: `Rapid-Series-G .link2`,
                            __btn_id2: `https://${locale}.ecoflow.com/pages/warranty-policy`,
                            __btn_type: 'href',
                        }}
                    >
                        {text} <MoreIcon />
                    </EditorLink>
                ),
            }),
            richContent: t.rich('item2_rich', {
                tip: (text: string) => <span className="card-tip">{text}</span>,
            }),
        },
        {
            title: t('item3_title'),
            content: [t('item3_content1'), t('item3_content2')],
            icon: locale === 'jp' ? <Step7 /> : <Step3 />,
            learnMoreLink: t.rich('item3_learn_more', {
                link: (text: any) => (
                    <EditorLink
                        href={`https://${locale}.ecoflow.com/pages/warranty-policy`}
                        className="card-link GtmModuleData-btn-href link3 learn-more"
                        style={{ color: '#3F68E0' }}
                        target="_blank"
                        rel="noopener noreferrer"
                        dataLayer={{
                            __btn_id1: `Rapid-Series-G .link3`,
                            __btn_id2: `https://${locale}.ecoflow.com/pages/warranty-policy`,
                            __btn_type: 'href',
                        }}
                    >
                        {text} <MoreIcon />
                    </EditorLink>
                ),
            }),
        },
        {
            title: t('item4_title'),
            content: [t('item4_content1'), t('item4_content2'), t('item4_content3')],
            icon: locale === 'jp' ? <Step8 /> : <Step4 />,
            richContent: t.rich('item4_rich', {
                signup: (text: any) => (
                    <EditorLink
                        href="https://account.ecoflow.com/register"
                        className="card-link GtmModuleData-btn-href link4 underline-link"
                        style={{ color: '#3F68E0' }}
                        target="_blank"
                        rel="noopener noreferrer"
                        dataLayer={{
                            __btn_id1: `Rapid-Series-G .link4`,
                            __btn_id2: `https://account.ecoflow.com/register`,
                            __btn_type: 'href',
                        }}
                        onClick={() => signupCookie({ sector_name: t('title'), entry_position: 6 })}
                    >
                        {text}
                    </EditorLink>
                ),
                login: (text: any) => (
                    <EditorLink
                        href="https://account.ecoflow.com/login"
                        className="card-link GtmModuleData-btn-href link5 underline-link"
                        style={{ color: '#3F68E0' }}
                        target="_blank"
                        rel="noopener noreferrer"
                        dataLayer={{
                            __btn_id1: `Rapid-Series-G .link5`,
                            __btn_id2: `https://account.ecoflow.com/login`,
                            __btn_type: 'href',
                        }}
                        onClick={() => signupCookie({ sector_name: t('title'), entry_position: 6 })}
                    >
                        {text}
                    </EditorLink>
                ),
                tip: (text: any) => <span className="card-tip">{text}</span>,
            }),
        },
    ]);

    const swiperRef = useRef<any>(null);

    const handleScroll = (direction: 'left' | 'right') => {
        if (swiperRef.current) {
            if (direction === 'left') {
                swiperRef.current.slidePrev();
            } else {
                swiperRef.current.slideNext();
            }
            if (pageStatus === IPageStatus.statusA) {
                // 预热页：编号 109
                gtm.push({
                    event: 'uaEvent',
                    event_name: 'product_preview_page',
                    list_name: t('title'),
                    button_name: direction === 'left' ? 'Left switch' : 'Right switch',
                    __btn_id1: `Rapid-Series-G .${direction === 'left' ? 'left' : 'right'}`,
                    __btn_id2: direction === 'left' ? '<' : '>',
                    __btn_type: 'text',
                });
            }
            if (pageStatus === IPageStatus.statusB) {
                // 正式页：编号 48
                gtm.push({
                    event: 'uaEvent',
                    event_name: 'product_detail_page',
                    list_name: t('title'),
                    button_name: direction === 'left' ? 'Left switch' : 'Right switch',
                    __btn_id1: `Rapid-Series-G .${direction === 'left' ? 'left' : 'right'}`,
                    __btn_id2: direction === 'left' ? '<' : '>',
                    __btn_type: 'text',
                });
            }
        }
    };

    useEffect(() => {
        if (typeof window !== 'undefined') {
            setGap(window.innerWidth >= 1024 ? 24 : 16);
        }
    }, []);

    return (
        <div className={`${css.thecomp} Rapid-Series-G GtmModuleSection-section`} data-section-id="Rapid-Series-G">
            <div className="g-container">
                <div className="dp3-title-container">
                    <div className="dp3-title">{t('title')}</div>
                    {/* 左右滑动的icon */}

                    <div className="scrollIcons">
                        <div className="scroll-icon GtmModuleData-btn-text left" onClick={() => handleScroll('left')}>
                            {/* <span className="icon icon-left"></span> */}
                            <EcoIconSwiperArrow className="eco-swiper-button-icon" />
                        </div>
                        <div className="scroll-icon GtmModuleData-btn-text right" onClick={() => handleScroll('right')}>
                            {/* <span className="icon icon-right"></span> */}
                            <EcoIconSwiperArrow className="eco-swiper-button-icon" />
                        </div>
                    </div>
                </div>
                <div className="comp-in comp-box">
                    <Swiper
                        className={`swiper-box`}
                        spaceBetween={gap}
                        slidesPerView={'auto'}
                        initialSlide={0}
                        onSwiper={(swiper) => (swiperRef.current = swiper)}
                    >
                        {list.map((item, index) => (
                            <SwiperSlide className="card" key={index}>
                                <div>
                                    <div className="card-icon">{item.icon}</div>
                                    <div className="card-title">{item.title}</div>
                                    {item.content && (
                                        <ul className="card-content">
                                            {item.content.map((text: string, i: number) => (
                                                <li key={i} className="card-content-item">
                                                    {text}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                    {item.learnMoreLink && <>{item.learnMoreLink}</>}
                                </div>
                                {item.richContent && <div className="card-link-container">{item.richContent}</div>}
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>
            </div>
        </div>
    );
};

export default TheComp;
